/**
 * Operations Calendar Types
 *
 * Type definitions for calendar events combining projects, tasks, and milestones.
 */

// =============================================================================
// Event Types
// =============================================================================

// Note: Milestones were removed from calendar view - they now appear in the project activity log
export type CalendarEventType = 'project' | 'task';
export type ProjectType = 'installation' | 'shipping' | 'calibration';
export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type ProjectStatus = 'draft' | 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

// =============================================================================
// Calendar Event Interface
// =============================================================================

export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  type: CalendarEventType;
  sourceId: string;
  sourceType: ProjectType | 'task';
  status: string;
  priority?: TaskPriority;
  assigneeId?: string;
  assigneeName?: string;
  projectNumber?: string;
  backgroundColor: string;
  borderColor: string;
  textColor?: string;
  extendedProps: {
    accountName?: string;
    description?: string;
    milestoneKey?: string;
    progress?: number;
  };
}

// =============================================================================
// API Response Types (V4 - camelCase from backend)
// =============================================================================

export interface V4CalendarEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  type: CalendarEventType;
  sourceId: string;
  sourceType: ProjectType | 'task';
  status: string;
  priority?: TaskPriority;
  assigneeId?: string;
  assigneeName?: string;
  projectNumber?: string;
  backgroundColor: string;
  borderColor: string;
  textColor?: string;
  extendedProps: {
    accountName?: string;
    description?: string;
    milestoneKey?: string;
    progress?: number;
  };
}

export interface V4CalendarEventsResponse {
  events: V4CalendarEvent[];
  total: number;
}

export interface V4RescheduleResponse {
  success: boolean;
  data: Record<string, unknown>;
}

// =============================================================================
// Query Parameters
// =============================================================================

export interface CalendarEventsParams {
  start: string;
  end: string;
  types?: string;
  projectTypes?: string;
  assigneeIds?: string;
  includeCompleted?: boolean;
}

// =============================================================================
// Filter State
// =============================================================================

export interface CalendarFilters {
  types: CalendarEventType[];
  projectTypes: ProjectType[];
  assigneeIds: string[];
  includeCompleted: boolean;
}

export const DEFAULT_CALENDAR_FILTERS: CalendarFilters = {
  types: ['project', 'task'],
  projectTypes: ['installation', 'shipping', 'calibration'],
  assigneeIds: [],
  includeCompleted: false,
};

// =============================================================================
// Reschedule Input
// =============================================================================

export interface RescheduleInput {
  id: string;
  type: 'project' | 'task';
  newStart: string;
}

// =============================================================================
// Calendar View Types
// =============================================================================

export type CalendarViewType = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

export interface DateRange {
  start: Date;
  end: Date;
}
